import { Color, Spacing } from '@persona-id/design-tokens';
import { startCase } from 'lodash';
import { VFC } from 'react';
import styled, { css } from 'styled-components';

import { NAVBAR_COLLAPSED_WIDTH, NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import LabeledFieldInput from 'dashboard/components-lib/LabeledFieldInput';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { WorkflowRunSimulateContext } from 'dashboard/models/workflow-run-simulated';
import {
  TRIGGER_DEFINITION_PANEL_SUPPORTED_SCHEMA_TYPES,
  WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH,
} from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/constants';
import { FieldSchema } from 'lib/fields';
import useNavbarToggle from 'lib/hooks/useNavbarToggle';

const CONTAINER_STYLE = css`
  padding: ${Spacing[1]};
`;

const Container = styled.div`
  border-top: 1px solid ${Color.CoreSlate100};
  padding-top: ${Spacing[4]};
  padding-bottom: ${Spacing[4]};
`;

interface TriggerFieldDefinitionPanelProps {
  closePanel: () => void;
  setSimulateContext: (arg: WorkflowRunSimulateContext) => void;
  simulateContext: WorkflowRunSimulateContext;
  fieldSchemas: FieldSchema[];
  disabled: boolean;
  triggerType: 'inquiry' | 'case' | 'transaction';
  requiredFields: string[];
  referencedFields: string[];
}

const TriggerFieldDefinitionPanel: VFC<TriggerFieldDefinitionPanelProps> = ({
  closePanel,
  setSimulateContext,
  simulateContext,
  fieldSchemas,
  disabled,
  triggerType,
  requiredFields,
  referencedFields,
}) => {
  const [navBarCollapsed] = useNavbarToggle();

  const handleFieldUpdate = (value: string | null, key: string) => {
    const overrides = simulateContext?.trigger?.overrides || {};
    //remove a field from an override if its input is cleared (value is falsy)
    const updatedOverrides = value
      ? { ...overrides, [`fields.${key}`]: value }
      : Object.fromEntries(Object.entries(overrides).filter(([k]) => k !== `fields.${key}`));

    setSimulateContext({
      ...simulateContext,
      trigger: {
        ...simulateContext?.trigger,
        overrides: updatedOverrides,
        type: triggerType,
      },
    });
  };

  return (
    <MaximizablePanel
      panelPosition='left'
      title={`${startCase(triggerType)} field details`}
      subtitle={`Simulate fields by specifying ${startCase(triggerType)} details.`}
      containerStyleOverride={CONTAINER_STYLE}
      onOutsideClickAndEscPress={closePanel}
      onClosePanel={closePanel}
      blockedLeft={
        (navBarCollapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_EXPANDED_WIDTH) +
        WORKFLOW_SIMULATE_CONTROL_PANEL_WIDTH +
        'px'
      }
    >
      <Container>
        {fieldSchemas &&
          fieldSchemas
            .filter(
              (schema) =>
                TRIGGER_DEFINITION_PANEL_SUPPORTED_SCHEMA_TYPES.includes(schema.type) &&
                !schema.config['deactivated-at'],
            )
            .map((schema) => {
              return (
                <LabeledFieldInput
                  key={schema.key}
                  fieldSchema={schema}
                  disabled={disabled}
                  onChange={handleFieldUpdate}
                  id={`field_input.${schema.key}`}
                  value={simulateContext.trigger?.overrides?.[`fields.${schema.key}`]}
                  required={requiredFields.includes(schema.key)}
                  referenced={referencedFields.includes(schema.key)}
                />
              );
            })}
      </Container>
    </MaximizablePanel>
  );
};

export default TriggerFieldDefinitionPanel;
